import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Handler from "../../Helpers/Handler";
import MainLayout from "../../Partials/MainLayout";
import FeedLayout from "../../Partials/FeedLayout";
import "../../Assets/css/style.css";

const BASE_URL = "https://qnexus.in/community/";
// const BASE_URL = "http://localhost/business_community/";
const FALLBACK = "https://ui-avatars.com/api/?background=4f46e5&color=fff&name=";

/* ── helpers ─────────────────────────────────── */
const ago = (dateStr) => {
    if (!dateStr) return "";
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const getAvatar = (name, logo) =>
    logo ? BASE_URL + logo : `${FALLBACK}${encodeURIComponent(name || "U")}`;

/* ══════════════════════════════════════════════
   ChatList
══════════════════════════════════════════════ */
const ChatList = () => {
    const navigate = useNavigate();
    const currentUser = useSelector((s) => s.customer?.customer?.user);

    /* ── state ─────────────────────────────── */
    const [chats, setChats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [error, setError] = useState("");

    // new-chat modal
    const [showModal, setShowModal] = useState(false);
    const [bizSearch, setBizSearch] = useState("");
    const [bizResults, setBizResults] = useState([]);
    const [bizLoading, setBizLoading] = useState(false);
    const [startingId, setStartingId] = useState(null);

    // delete confirmation
    const [deleteTarget, setDeleteTarget] = useState(null); // chat_id to delete
    const [deleting, setDeleting] = useState(false);

    const pollRef = useRef(null);
    const bizTimer = useRef(null);

    /* ── fetch chat list ─────────────────────── */
    const fetchChats = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await Handler({ method: "get", url: "/chat/list.php" });
            if (res?.success) { setChats(res.data?.list || []); setError(""); }
            else setError(res?.message || "Failed to load chats.");
        } catch { setError("Network error. Please try again."); }
        if (!silent) setLoading(false);
    }, []);

    useEffect(() => {
        fetchChats();
        pollRef.current = setInterval(() => fetchChats(true), 10000);
        return () => clearInterval(pollRef.current);
    }, [fetchChats]);

    /* ── search businesses (new chat) ────────── */
    const searchBusinesses = useCallback(async (q) => {
        if (!q.trim()) { setBizResults([]); return; }
        setBizLoading(true);
        try {
            const res = await Handler({
                method: "get",
                url: `/users/search_list.php?search=${encodeURIComponent(q)}&limit=20`
            });
            if (res?.success) setBizResults(res.data?.list || []);
            else setBizResults([]);
        } catch { setBizResults([]); }
        setBizLoading(false);
    }, []);

    const handleBizSearchChange = (e) => {
        const val = e.target.value;
        setBizSearch(val);
        clearTimeout(bizTimer.current);
        bizTimer.current = setTimeout(() => searchBusinesses(val), 400);
    };

    /* ── start / open chat ───────────────────── */
    const startChat = async (userId) => {
        setStartingId(userId);
        try {
            const res = await Handler({
                method: "post", url: "/chat/start.php",
                data: { user_id: userId },
            });
            if (res?.success) {
                const chatId = res.data.chat_id;
                const otherBiz = res.data.other_business;
                setShowModal(false);
                setBizSearch("");
                setBizResults([]);
                navigate("/user/chat", {
                    state: {
                        chat_id: chatId,
                        other_user: {
                            user_id: userId,
                            name: otherBiz?.business_name,
                            logo: null,
                            is_verified: false,
                        },
                    },
                });
            }
        } catch { }
        setStartingId(null);
    };

    const openChat = (chat) => {
        navigate("/user/chat", {
            state: { chat_id: chat.chat_id, other_user: chat.other_user },
        });
    };

    /* ── delete chat ─────────────────────────── */
    const confirmDelete = (e, chatId) => {
        e.stopPropagation();
        setDeleteTarget(chatId);
    };

    const doDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const res = await Handler({
                method: "post", url: "/chat/delete.php",
                data: { action: "delete_chat", chat_id: deleteTarget },
            });
            if (res?.success) {
                setChats((prev) => prev.filter((c) => c.chat_id !== deleteTarget));
            }
        } catch { }
        setDeleting(false);
        setDeleteTarget(null);
    };

    /* ── filtered list ───────────────────────── */
    const filtered = chats.filter((c) =>
        (c.other_user?.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (c.last_message || "").toLowerCase().includes(search.toLowerCase())
    );

    /* ── render ─────────────────────────────── */
    return (
        <MainLayout>
            <FeedLayout>
                <div className="cl-page">

                    {/* ── Top bar ──────────────────────── */}
                    <div className="cl-topbar">
                        <div className="cl-title-row">
                            <div className="cl-title">
                                
                                <span>Messages</span>
                                {chats.length > 0 && <span className="cl-count">{chats.length}</span>}
                            </div>

                            <button className="cl-new-btn" onClick={() => setShowModal(true)}>
                                <i className="bx bx-edit" />
                                <span>New Chat</span>
                            </button>
                        </div>

                        <div className="cl-search-wrap">
                            <i className="bx bx-search cl-search-icon" />
                            <input
                                className="cl-search"
                                type="text"
                                placeholder="Search conversations…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            {search && (
                                <button className="cl-search-clear" onClick={() => setSearch("")}>
                                    <i className="bx bx-x" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* ── Error ────────────────────────── */}
                    {error && (
                        <div className="cl-error">
                            <i className="bx bx-error-circle" />{error}
                        </div>
                    )}

                    {/* ── Body ─────────────────────────── */}
                    <div className="cl-body">

                        {/* Skeleton */}
                        {loading && (
                            <div className="cl-skeletons">
                                {[...Array(5)].map((_, i) => (
                                    <div key={i} className="cl-skeleton">
                                        <div className="sk-avatar" />
                                        <div className="sk-lines">
                                            <div className="sk-line sk-name" />
                                            <div className="sk-line sk-msg" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Empty */}
                        {!loading && filtered.length === 0 && (
                            <div className="cl-empty">
                                <div className="cl-empty-icon"><i className="bx bx-message-x" /></div>
                                <p className="cl-empty-title">
                                    {search ? "No results found" : "No conversations yet"}
                                </p>
                                <p className="cl-empty-sub">
                                    {search
                                        ? `No chats match "${search}"`
                                        : "Click \"New Chat\" to start a conversation."}
                                </p>
                            </div>
                        )}

                        {/* Chat list */}
                        {!loading && filtered.length > 0 && (
                            <ul className="cl-list">
                                {filtered.map((chat) => (
                                    <li
                                        key={chat.chat_id}
                                        className="cl-item"
                                        onClick={() => openChat(chat)}
                                    >
                                        {/* Avatar */}
                                        <div className="cl-avatar-wrap">
                                            <img
                                                className="cl-avatar"
                                                src={getAvatar(chat.other_user?.name, chat.other_user?.logo)}
                                                alt={chat.other_user?.name || "User"}
                                                onError={(e) => {
                                                    e.target.src = `${FALLBACK}${encodeURIComponent(chat.other_user?.name || "U")}`;
                                                }}
                                            />
                                            {chat.other_user?.is_verified && (
                                                <span className="cl-verified">
                                                    <i className="bx bxs-badge-check" />
                                                </span>
                                            )}
                                        </div>

                                        {/* Info */}
                                        <div className="cl-info">
                                            <div className="cl-info-top">
                                                <span className="cl-name">{chat.other_user?.name || "Unknown"}</span>
                                                <span className="cl-time">{ago(chat.last_message_at)}</span>
                                            </div>
                                            <div className="cl-info-bot">
                                                <span className="cl-preview">
                                                    {chat.last_message || <em className="cl-no-msg">No messages yet</em>}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Delete button */}
                                        <button
                                            className="cl-delete-btn"
                                            title="Delete chat"
                                            onClick={(e) => confirmDelete(e, chat.chat_id)}
                                        >
                                            <i className="bx bx-trash" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* ══ New Chat Modal ═══════════════════ */}
                {showModal && (
                    <div className="cl-modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="cl-modal-header">
                                <h3><i className="bx bx-edit-alt" /> New Conversation</h3>
                                <button className="cl-modal-close" onClick={() => setShowModal(false)}>
                                    <i className="bx bx-x" />
                                </button>
                            </div>

                            <div className="cl-modal-search-wrap">
                                <i className="bx bx-search" />
                                <input
                                    autoFocus
                                    type="text"
                                    placeholder="Search business name…"
                                    value={bizSearch}
                                    onChange={handleBizSearchChange}
                                />
                                {bizLoading && <i className="bx bx-loader-alt bx-spin cl-modal-spin" />}
                            </div>

                            <div className="cl-modal-results">
                                {!bizSearch && (
                                    <p className="cl-modal-hint">
                                        <i className="bx bx-info-circle" /> Type a business name to search
                                    </p>
                                )}

                                {bizSearch && !bizLoading && bizResults.length === 0 && (
                                    <p className="cl-modal-hint">No businesses found for "{bizSearch}"</p>
                                )}

                                {bizResults.map((biz) => (
                                    <div
                                        key={biz.id}                        // bp.id — always unique
                                        className="cl-modal-item"
                                        onClick={() => startChat(biz.user_id)}  // user_id for startChat
                                    >
                                        <img
                                            className="cl-modal-avatar"
                                            src={getAvatar(biz.business_name, biz.logo)}
                                            alt={biz.business_name}
                                            onError={(e) => {
                                                e.target.src = `${FALLBACK}${encodeURIComponent(biz.business_name || "B")}`;
                                            }}
                                        />
                                        <div className="cl-modal-info">
                                            <span className="cl-modal-name">{biz.business_name}</span>
                                        </div>
                                        {startingId === biz.user_id ? (
                                            <i className="bx bx-loader-alt bx-spin cl-modal-go" />
                                        ) : (
                                            <i className="bx bx-chevron-right cl-modal-go" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* ══ Delete confirm dialog ════════════ */}
                {deleteTarget && (
                    <div className="cl-modal-overlay" onClick={() => setDeleteTarget(null)}>
                        <div className="cl-confirm" onClick={(e) => e.stopPropagation()}>
                            <div className="cl-confirm-icon"><i className="bx bx-trash" /></div>
                            <h4>Delete Conversation?</h4>
                            <p>This will remove the chat from your list. The other person's copy will remain.</p>
                            <div className="cl-confirm-btns">
                                <button className="cl-confirm-cancel" onClick={() => setDeleteTarget(null)}>
                                    Cancel
                                </button>
                                <button className="cl-confirm-ok" onClick={doDelete} disabled={deleting}>
                                    {deleting ? <i className="bx bx-loader-alt bx-spin" /> : "Delete"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </FeedLayout>
        </MainLayout>
    );
};

export default ChatList;